import React, { useEffect, useState, memo } from 'react';
import { Star, Users, Calendar, Award, MessageSquare, Instagram, Facebook, Globe, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function TrustIndex() {
  const metrics = [{
    label: 'Global Enthusiasts',
    value: '12k+',
    icon: Users
  }, {
    label: 'Curated Events',
    value: '450+',
    icon: Calendar
  }, {
    label: 'Industry Awards',
    value: '24',
    icon: Award
  }, {
    label: 'Public Reviews',
    value: '1,800+',
    icon: MessageSquare
  }];
  const platforms = [{
    name: 'Google',
    score: '4.9',
    reviews: '842',
    icon: Globe
  }, {
    name: 'Facebook',
    score: '5.0',
    reviews: '420',
    icon: Facebook
  }, {
    name: 'Instagram',
    score: 'Engagement',
    reviews: 'High',
    icon: Instagram
  }];
  const feedback = [{
    platform: 'Grains & Grapes MD',
    user: 'Jasmine H.',
    role: 'Member',
    text: 'Celebrating my 35th birthday with Cork Dorx was truly one of the most memorable experiences I’ve had. Treena is more than just a wine educator; she has genuinely become a friend. Her passion for wine is contagious, and the way she teaches makes learning feel exciting, fun, and personal instead of overwhelming. The private tasting was the perfect blend of education, laughter, culture, and celebration.',
    date: 'Recent Member',
    location: 'Arlington 2025'
  }, {
    platform: 'Urbanique LLC',
    user: 'Stephanie C.',
    role: 'Owner',
    text: 'We had an amazing experience with Cork Dorx during our Knit & Sip collaboration. The wine tasting service was professional, engaging, and perfectly curated for our guests. Treena was extremely knowledgeable about each wine selection and created such a warm, fun, and welcoming atmosphere throughout the event. I highly recommend Cork Dorx for any social gathering or private event!',
    date: 'Event Partner',
    location: 'Temecula 2024'
  }, {
    platform: 'Washington Property Company',
    user: 'Kittim Muthomi',
    role: 'Former Property Manager',
    text: 'Working with Cork Dorx, LLC was honestly one of the best event experiences I’ve had professionally. Treena offered not only her services but also the vision, coordination, and confidence needed to bring the event to life. Her signature cocktail creations were a huge hit - guests kept going back for more! I would confidently recommend Cork Dorx to anyone looking to host a high-quality event.',
    date: 'Professional Client',
    location: 'Washington DC 2024'
  }];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const nextSlide = () => {
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % feedback.length);
  };
  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + feedback.length) % feedback.length);
  };
  return <section className="bg-[#2D0909] py-20 px-6 overflow-hidden relative" data-id="element-127">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-[#4A0E0E]/10 skew-x-12 translate-x-1/4 pointer-events-none" data-id="element-128" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-129">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20" data-id="element-130">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-131">
            <span className="text-[#D4AF37] font-bold uppercase tracking-[0.4em] text-[10px] mb-4 block" data-id="element-132">
              Reputation & Trust
            </span>
            <h2 className="text-white text-3xl md:text-4xl mb-6 leading-tight" data-id="element-133">
              What Our <br data-id="element-134" />
              <span className="text-[#D4AF37] italic" data-id="element-135">Customers Say</span>
            </h2>
            <p className="text-white/60 text-base max-w-md leading-relaxed" data-id="element-136">
              Our reputation is built on the voices of our community. We take
              pride in maintaining the highest levels of excellence across every
              experience.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-3xl" data-id="element-137">
            <div className="space-y-6" data-id="element-138">
              {platforms.map((platform, idx) => <div key={platform.name} className="flex items-center justify-between group" data-id="element-139">
                  <div className="flex items-center gap-4" data-id="element-140">
                    <div className="p-3 bg-white/5 rounded-xl text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#2D0909] transition-all duration-300" data-id="element-141">
                      <platform.icon size={20} data-id="element-142" />
                    </div>
                    <div data-id="element-143">
                      <h4 className="text-white font-serif text-lg mb-0.5" data-id="element-144">
                        {platform.name}
                      </h4>
                      <p className="text-white/30 text-[9px] uppercase tracking-widest" data-id="element-145">
                        {platform.reviews} Reviews
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-146">
                    <div className="flex items-center gap-1.5 text-[#D4AF37]" data-id="element-147">
                      <Star size={12} fill="currentColor" data-id="element-148" />
                      <span className="text-xl font-serif font-bold text-white" data-id="element-149">
                        {platform.score}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20" data-id="element-150">
          {metrics.map((metric, idx) => <motion.div key={metric.label} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: idx * 0.05
        }} className="text-center p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-[#D4AF37]/20 transition-colors" data-id="element-151">
              <div className="text-[#D4AF37] mb-4 flex justify-center" data-id="element-152">
                <metric.icon size={24} strokeWidth={1} data-id="element-153" />
              </div>
              <div className="text-3xl font-serif text-white mb-2" data-id="element-154">
                {metric.value}
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-white/30 font-bold" data-id="element-155">
                {metric.label}
              </div>
            </motion.div>)}
        </div>

        {/* Testimonials Slider */}
        <div className="relative mb-20" data-id="element-156">
          <div className="max-w-4xl mx-auto px-8" data-id="element-157">
            <div className="text-center mb-8" data-id="element-158">
              <span className="text-[#D4AF37] font-bold uppercase tracking-[0.4em] text-[9px]" data-id="element-159">
                The Experience
              </span>
            </div>

            <div className="relative min-h-[300px] flex items-center justify-center" data-id="element-160">
              <AnimatePresence mode="wait" custom={direction} data-id="element-161">
                <motion.div key={currentIndex} custom={direction} initial={{
                opacity: 0,
                x: direction * 50
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: direction * -50
              }} transition={{
                duration: 0.5,
                ease: 'easeInOut'
              }} className="w-full text-center" data-id="element-162">
                  <p className="text-xl md:text-2xl font-serif text-white mb-8 leading-relaxed italic max-w-3xl mx-auto" data-id="element-163">
                    "{feedback[currentIndex].text}"
                  </p>

                  <div className="flex flex-col items-center gap-3" data-id="element-164">
                    <div className="flex items-center gap-1 mb-2" data-id="element-165">
                      {[1, 2, 3, 4, 5].map(s => <Star key={s} size={12} fill="#D4AF37" className="text-[#D4AF37]" data-id="element-166" />)}
                    </div>
                    <div className="text-[#D4AF37] font-bold uppercase tracking-[0.2em] text-[10px]" data-id="element-167">
                      — {feedback[currentIndex].user.toUpperCase()},{' '}
                      {feedback[currentIndex].location.toUpperCase()}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition-all bg-white/5" aria-label="Previous testimonial" data-id="element-168">
                <ChevronLeft size={20} data-id="element-169" />
              </button>
              <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition-all bg-white/5" aria-label="Next testimonial" data-id="element-170">
                <ChevronRight size={20} data-id="element-171" />
              </button>
            </div>

            {/* Pagination Dots */}
            <div className="flex items-center justify-center gap-4 mt-10" data-id="element-172">
              <div className="flex gap-1.5" data-id="element-173">
                {feedback.map((_, idx) => <button key={idx} onClick={() => {
                setDirection(idx > currentIndex ? 1 : -1);
                setCurrentIndex(idx);
              }} className={`h-1 transition-all duration-300 rounded-full ${idx === currentIndex ? 'w-6 bg-[#D4AF37]' : 'w-1 bg-white/20'}`} data-id="element-174" />)}
              </div>
              <div className="text-white/10 text-[9px] font-bold tracking-widest" data-id="element-175">
                0{currentIndex + 1} / 0{feedback.length}
              </div>
            </div>
          </div>
        </div>

        {/* Social Proof Badges */}
        <div className="pt-12 border-t border-white/10 flex flex-wrap justify-center items-center gap-10 opacity-30 hover:opacity-80 transition-all duration-700" data-id="element-176">
          <div className="text-[9px] font-bold uppercase tracking-[0.4em] text-[#D4AF37]" data-id="element-177">
            Recognized By
          </div>
          <div className="font-serif text-xl text-white/70 hover:text-white transition-colors" data-id="element-178">
            WineSpectator
          </div>
          <div className="font-serif text-xl text-white/70 hover:text-white transition-colors" data-id="element-179">
            Decanter
          </div>
          <div className="font-serif text-xl text-white/70 hover:text-white transition-colors" data-id="element-180">
            JamesSuckling
          </div>
          <div className="font-serif text-xl text-white/70 hover:text-white transition-colors" data-id="element-181">
            Vivino
          </div>
        </div>
      </div>
    </section>;
}